import { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const prevHashRef = useRef(location.hash);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to section on hash change (for direct hash URL access or same-page clicks)
  useEffect(() => {
    if (!isHome || !location.hash) return;
    if (location.hash === prevHashRef.current && prevHashRef.current !== "") return;
    prevHashRef.current = location.hash;

    const id = location.hash.replace("#", "");
    let attempts = 0;
    const maxAttempts = 20;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(tryScroll, 100);
      }
    };
    setTimeout(tryScroll, 200);
  }, [isHome, location.hash]);

  // Scroll to section from location state (cross-page navigation)
  useEffect(() => {
    const state = location.state as { faScrollTo?: string } | null;
    if (isHome && state?.faScrollTo) {
      const id = state.faScrollTo;
      // Clear state so it doesn't re-trigger on back/forward
      navigate("/#" + id, { replace: true, state: {} });
      let attempts = 0;
      const maxAttempts = 20;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(tryScroll, 100);
        }
      };
      setTimeout(tryScroll, 200);
    }
  }, [isHome]);

  const solid = scrolled || !isHome;

  const links = [
    { label: "HOME", to: "/" },
    { label: "GALLERY", to: "/gallery" },
    { label: "ARTISTS", to: "/#artists" },
    { label: "PORTFOLIO", to: "/#portfolio" },
    { label: "BOOKING", to: "/#booking" },
    { label: "VISIT", to: "/#visit" },
  ];

  const textColor = solid ? "text-foreground-950" : "text-background-50";
  const bookDot = solid ? "bg-primary-500" : "bg-primary-500";
  const mobileBg = solid
    ? "bg-background-50 border-foreground-950/10"
    : "bg-background-100/20 border-foreground-950/10 backdrop-blur-md";

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const handleHashClick = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHome) {
      scrollToSection(sectionId);
    } else {
      navigate("/", { state: { faScrollTo: sectionId } });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-background-50/95 backdrop-blur-sm border-b border-foreground-950/5" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-12 py-5">
        <Link to="/" className="flex items-center gap-3 group cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/8c148353-8b70-4bbb-b6e9-ba9d42f8c8c2/2c1dd30f-e262-4d25-82a4-4cbee6af241f_compressed_IMG_5651-1.webp"
            alt="Fine Arts Tattoo Collective Logo"
            className="w-9 h-9 rounded-full object-cover"
          />
          <span
            className={`hidden sm:block font-display font-semibold tracking-tight text-sm uppercase transition-colors duration-500 ${textColor}`}
          >
            Fine Arts Tattoo collective
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10 absolute left-1/2 -translate-x-1/2">
          {links.map((link) => {
            const isHash = link.to.startsWith("/#");
            const sectionId = isHash ? link.to.replace("/#", "") : null;
            return (
              <Link
                key={link.label}
                to={link.to}
                onClick={isHash && sectionId ? (e) => handleHashClick(e, sectionId) : () => setMenuOpen(false)}
                className={`link-underline font-display text-[12px] uppercase tracking-[0.18em] cursor-pointer whitespace-nowrap transition-colors duration-500 ${textColor} hover:opacity-70 opacity-90`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        <Link
          to="/#booking"
          onClick={(e) => handleHashClick(e, "booking")}
          className={`hidden lg:flex items-center gap-3 font-display text-[12px] uppercase tracking-[0.22em] cursor-pointer transition-colors duration-500 ${textColor}`}
        >
          <span
            className={`w-2 h-2 rounded-full ${bookDot} transition-colors duration-500`}
          ></span>
          Book
        </Link>

        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-500 ${textColor}`}
          aria-label="Menu"
        >
          <i className={`ri-${menuOpen ? "close" : "menu-3"}-line text-2xl`}></i>
        </button>
      </div>

      {menuOpen && (
        <div
          className={`lg:hidden border-t px-6 py-6 transition-colors duration-500 ${mobileBg}`}
        >
          <nav className="flex flex-col gap-5">
            {links.map((link) => {
              const isHash = link.to.startsWith("/#");
              const sectionId = isHash ? link.to.replace("/#", "") : null;
              return (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={isHash && sectionId ? (e) => handleHashClick(e, sectionId) : () => setMenuOpen(false)}
                  className={`font-display text-base uppercase tracking-wider cursor-pointer ${textColor}`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}